import {QuestionModelV3, SubjectModelV2, SupplementModelV1} from '@albert-io/models';
import makeConstants from 'lib/makeConstants';
import {List} from 'immutable';

import {
  TranslatedQuestion,
  TranslatedSupplement,
  // AlternateContentLanguage,
  // ALTERNATE_CONTENT_LANGUAGES,
  TranslationCoverage
} from './Translation.types';

const questionTypesArr = [
  'multiple-choice',
  'snippet-selection',
  'free-entry',
  'free-response',
  'fill-in-the-blank',
  'two-way',
  'passage-correction',
  'text-highlight',
  'graph-contains'
] as const;

type QuestionTypes = (typeof questionTypesArr)[number];

const questionTypes = makeConstants(...questionTypesArr);

interface modelPayload {
  question: QuestionModelV3;
  subject: SubjectModelV2;
  supplements: List<SupplementModelV1>;
}

export const getTranslationCoverage = (
  translatedInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
): TranslationCoverage => {
  const {question} = models;

  const type: QuestionTypes = question.getQuestionType();

  switch (type) {
    case questionTypes['multiple-choice']:
      return checkMultipleChoiceFields(translatedInfo, translatedSupplements, models);
    case questionTypes['snippet-selection']:
      return checkSnippetSelectionFields(translatedInfo, translatedSupplements, models);
    case questionTypes['free-entry']:
      return checkFreeEntryFields(translatedInfo, translatedSupplements, models);
    case questionTypes['free-response']:
      return checkFreeResponseFields(translatedInfo, translatedSupplements, models);
    case questionTypes['fill-in-the-blank']:
      return checkFillImBlankFields(translatedInfo, translatedSupplements, models);
    case questionTypes['two-way']:
      return checkTwoWayFields(translatedInfo, translatedSupplements, models);
    case questionTypes['passage-correction']:
      return checkPassageCorrectionFields(translatedInfo, translatedSupplements, models);
    case questionTypes['text-highlight']:
      return checkTextHighlightFields(translatedInfo, translatedSupplements, models);
    case questionTypes['graph-contains']:
      return checkGraphFields(translatedInfo, translatedSupplements, models);
    default:
      return defaultTranslationCoverage(translatedInfo);
  }
};

const checkMultipleChoiceFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {question, supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const translatedOptionFields = getOptionsInfo(translationInfo.translated_fields);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );

  const options = question.getOptions();
  const hasTranslatedOptions = translatedOptionFields.length === options.size;

  if (translationInfo.translated_fields.length > 1) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasTranslatedOptions &&
      hasSupplementTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }
  return 'none';
};

const checkSnippetSelectionFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );
  const hasSnippetPromptTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'snippet_prompt'
  );

  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation &&
      hasSnippetPromptTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkFreeEntryFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );

  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkFreeResponseFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );

  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation
    ) {
      return 'full';
    }
    return 'partial';
  }

  return 'none';
};

const checkFillImBlankFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {question, supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );
  const hadDropdownText = translationInfo.translated_fields.some((info) =>
    info.field.includes('dropdown_text')
  );

  const dropdownChoices = getQuestionOptionChoices(question);
  const translatedDropdownChoiceFields = getDropdownsInfo(translationInfo.translated_fields);
  const hasTranslatedOptions = translatedDropdownChoiceFields.length === dropdownChoices.size;

  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation &&
      hadDropdownText &&
      hasTranslatedOptions
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkTwoWayFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );

  const hasHeaderLeftTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'header_left'
  );
  const hasHeaderRightTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'header_right'
  );

  const hasHeaderTranslations = hasHeaderLeftTranslation && hasHeaderRightTranslation;

  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation &&
      hasHeaderTranslations
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkPassageCorrectionFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );
  const hasUncorrectedTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'uncorrected_text'
  );
  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation &&
      hasUncorrectedTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkTextHighlightFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );
  const hasHighlightPromptTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'highlight_prompt'
  );

  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation &&
      hasHighlightPromptTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkGraphFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const hasSupplementTranslation = getSupplementTranslationCoverage(
    translatedSupplements,
    supplements
  );

  if (translationInfo.translated_fields.length > 0) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasSupplementTranslation
    )
      return 'full';

    return 'partial';
  }

  return 'none';
};

const hasBasicFields = (translationInfo: TranslatedQuestion) => {
  const translatedFields = [...translationInfo.translated_fields];

  const hasPromptTranslation = translatedFields.some((info) => info.field === 'prompt');
  const hasTitleTranslation = translatedFields.some((info) => info.field === 'title');
  const hasSolutionTextTranslation = translatedFields.some(
    (info) => info.field === 'solution_text'
  );

  return hasPromptTranslation && hasTitleTranslation && hasSolutionTextTranslation;
};

const getOptionsInfo = (translatedFields: TranslatedQuestion['translated_fields']) => {
  return translatedFields.filter((info) => info.field.includes('option'));
};

const getDropdownsInfo = (translatedFields: TranslatedQuestion['translated_fields']) => {
  return translatedFields.filter((info) => {
    return info.field.includes('dropdowns');
  });
};

const getQuestionOptionChoices = (question: QuestionModelV3) => {
  const dropdowns = question.getDropdowns();
  let choices = List();
  dropdowns.forEach((dropdown) => {
    const dropdownChoices = dropdown.getChoices();
    choices = choices.push(...dropdownChoices.toJS());
  });

  return choices;
};

const defaultTranslationCoverage = (translationInfo: any) => {
  if (translationInfo.meets_translation_requirements) return 'full';
  // if false
  if (translationInfo.translated_fields.length > 0) return 'partial';
  return 'none';
};

export const getSupplementTranslationCoverage = (
  translatedSupplements: TranslatedSupplement[],
  supplements
) => {
  const correctlyTranslatedSupplements = translatedSupplements.filter(
    ({meets_translation_requirements}) => meets_translation_requirements
  );

  return correctlyTranslatedSupplements.length === supplements.size;
};
